import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number;
  company_name: string;
}

interface StatementRow {
  id: number;
  date_str: string;
  debit: number;
  credit: number;
  balance: number;
  mapped_invoice_id?: number | null;
  mapped_purchase_id?: number | null;
  company_id?: number | null;
  bank_account_id?: number | null;
  company_name?: string | null;
  bank_name?: string | null;
  account_number?: string | null;
}

interface Invoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  paid: number;
}

interface PurchaseInvoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  paid: number;
}

interface Loan {
  id: number;
  company_id: number;
  lender_name: string;
  total_amount: number;
}

interface FixedDeposit {
  id: number;
  company_id: number;
  fd_number: string;
  total_amount: number;
}

const BalanceSheetReport: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [rows, setRows] = useState<StatementRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchases, setPurchases] = useState<PurchaseInvoice[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [fds, setFds] = useState<FixedDeposit[]>([]);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [compRes, rowsRes, invRes, purRes, loanRes, fdRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/bank-statement-rows`),
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/loans`),
        axios.get(`${API_URL}/auth/fixed-deposits`)
      ]);
      setCompanies(compRes.data || []);
      setRows(rowsRes.data || []);
      setInvoices(invRes.data || []);
      setPurchases(purRes.data || []);
      setLoans(loanRes.data || []);
      setFds(fdRes.data || []);
    } catch (err) {
      console.error("Failed to load balance sheet data:", err);
    } finally {
      setLoading(false);
    }
  };

  const bsData = useMemo(() => {
    // 1. Filtered datasets
    const filteredInvoices = invoices.filter(inv => !selectedCompanyId || String(inv.Company_id) === String(selectedCompanyId));
    const filteredPurchases = purchases.filter(pur => !selectedCompanyId || String(pur.Company_id) === String(selectedCompanyId));
    const filteredRows = rows.filter(r => !selectedCompanyId || String(r.company_id) === String(selectedCompanyId));
    const filteredLoans = loans.filter(l => !selectedCompanyId || String(l.company_id) === String(selectedCompanyId));
    const filteredFds = fds.filter(f => !selectedCompanyId || String(f.company_id) === String(selectedCompanyId));

    // 2. Assets
    // Cash & Bank (represented by the sum of last balances of each bank statement)
    const bankAccountsMap = new Map<string, { bankName: string; accountNumber: string; balance: number; companyName?: string }>();
    // Sort rows ascending by id to process chronologically, so the last one wins, OR process descending and first one wins
    // Since rows are ordered by id DESC in backend, the first row we find for an account is its latest.
    filteredRows.forEach(r => {
      const key = r.bank_account_id ? String(r.bank_account_id) : `${r.bank_name || ''}-${r.account_number || ''}`;
      if (!bankAccountsMap.has(key) && (r.bank_name || r.account_number)) {
        bankAccountsMap.set(key, {
          bankName: r.bank_name || 'Unknown Bank',
          accountNumber: r.account_number || '',
          balance: Number(r.balance || 0),
          companyName: r.company_name || undefined
        });
      }
    });

    const bankBalancesList = Array.from(bankAccountsMap.values());
    const bankBalance = bankBalancesList.reduce((sum, b) => sum + b.balance, 0);

    // Fixed Deposits
    const fixedDeposits = filteredFds.reduce((sum, f) => sum + Number(f.total_amount || 0), 0);

    // Accounts Receivable (Unpaid sales invoices)
    const accountsReceivable = filteredInvoices.reduce((sum, inv) => {
      if (inv.paid === 1) return sum; // fully paid
      // Otherwise subtract payments mapped to this invoice
      const paymentsSum = filteredRows
        .filter(r => r.mapped_invoice_id === inv.id)
        .reduce((s, r) => s + Number(r.credit || 0), 0);
      const outstanding = Number(inv.Total_Amount || 0) - paymentsSum;
      return sum + (outstanding > 0 ? outstanding : 0);
    }, 0);

    const totalAssets = bankBalance + fixedDeposits + accountsReceivable;

    // 3. Liabilities
    // Loans
    const loanLiabilities = filteredLoans.reduce((sum, l) => sum + Number(l.total_amount || 0), 0);

    // Accounts Payable (Unpaid bills)
    const accountsPayable = filteredPurchases.reduce((sum, pur) => {
      if (pur.paid === 1) return sum;
      const paymentsSum = filteredRows
        .filter(r => r.mapped_purchase_id === pur.id)
        .reduce((s, r) => s + Number(r.debit || 0), 0);
      const outstanding = Number(pur.Total_Amount || 0) - paymentsSum;
      return sum + (outstanding > 0 ? outstanding : 0);
    }, 0);

    const totalLiabilities = loanLiabilities + accountsPayable;

    // 4. Equity (Retained Earnings to balance the double entry sheet)
    const netEquity = totalAssets - totalLiabilities;

    return {
      bankBalance,
      bankBalancesList,
      fixedDeposits,
      filteredFds,
      accountsReceivable,
      totalAssets,
      loanLiabilities,
      filteredLoans,
      accountsPayable,
      totalLiabilities,
      netEquity
    };
  }, [selectedCompanyId, companies, rows, invoices, purchases, loans, fds]);

  return (
    <div className="bg-white shadow-xl border border-blue-100 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h3 className="font-extrabold text-blue-900 text-xl">Balance Sheet</h3>
          <p className="text-gray-500 text-xs">Overview of company assets, liabilities, and net equity.</p>
        </div>

        <div>
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            className="form-select border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white text-slate-800 focus:outline-none"
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 font-medium text-sm">Computing assets and liabilities...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto border border-blue-50 rounded-3xl overflow-hidden shadow-sm">
          
          {/* Left Column: ASSETS */}
          <div className="p-6 border-r border-blue-50">
            <h4 className="font-black text-blue-950 text-sm border-b-2 border-slate-350 pb-2 mb-4 uppercase tracking-wider">Assets</h4>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700">Cash & Bank Balances</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{bsData.bankBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="pl-4 py-1.5 space-y-1">
                  {bsData.bankBalancesList.length === 0 ? (
                    <span className="text-[10px] text-gray-400 italic">No bank balances</span>
                  ) : (
                    bsData.bankBalancesList.map((b, idx) => (
                      <div key={idx} className="flex justify-between text-[11px] text-slate-500 font-semibold">
                        <span>{b.bankName} ({b.accountNumber})</span>
                        <span>₹{b.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-700">Accounts Receivable (Clients)</span>
                <span className="text-sm font-extrabold text-slate-900">₹{bsData.accountsReceivable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>

              <div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700">Fixed Deposits Booked</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{bsData.fixedDeposits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="pl-4 py-1.5 space-y-1">
                  {bsData.filteredFds.length === 0 ? (
                    <span className="text-[10px] text-gray-400 italic">No Fixed Deposits</span>
                  ) : (
                    bsData.filteredFds.map((f, idx) => (
                      <div key={idx} className="flex justify-between text-[11px] text-slate-500 font-semibold">
                        <span>FD Number: {f.fd_number}</span>
                        <span>₹{f.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50/50 rounded-2xl p-4 flex justify-between items-center border border-blue-100 text-blue-950">
              <span className="text-sm font-bold uppercase">Total Assets</span>
              <span className="text-base font-black">₹{bsData.totalAssets.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Right Column: LIABILITIES & EQUITY */}
          <div className="p-6 bg-slate-50/20">
            <h4 className="font-black text-blue-950 text-sm border-b-2 border-slate-350 pb-2 mb-4 uppercase tracking-wider">Liabilities & Equity</h4>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-700">Accounts Payable (Vendors)</span>
                <span className="text-sm font-extrabold text-slate-900">₹{bsData.accountsPayable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>

              <div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700">Loans & Borrowings</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{bsData.loanLiabilities.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="pl-4 py-1.5 space-y-1">
                  {bsData.filteredLoans.length === 0 ? (
                    <span className="text-[10px] text-gray-400 italic">No active borrowings</span>
                  ) : (
                    bsData.filteredLoans.map((l, idx) => (
                      <div key={idx} className="flex justify-between text-[11px] text-slate-500 font-semibold">
                        <span>Lender: {l.lender_name}</span>
                        <span>₹{l.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-700">Net Equity & Retained Earnings</span>
                <span className="text-sm font-extrabold text-slate-900">₹{bsData.netEquity.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="mt-8 bg-blue-900 text-white rounded-2xl p-4 flex justify-between items-center shadow-md">
              <span className="text-sm font-bold uppercase">Total Liabilities & Equity</span>
              <span className="text-base font-black">₹{(bsData.totalLiabilities + bsData.netEquity).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default BalanceSheetReport;
